// PageHead (page patterns, BL-189): a page's title block (ui-web's DESIGN.md §3), the one every
// Blueprint page opens with. Where it is (the crumb: Place › Page, the place a link back), the title,
// one muted line under it, and at most two actions beside it (one primary), their bottoms on the
// line's. Under it, optionally, the figures (at most five) and the page's parts as tabs, the current
// one marked under in ink on the tabs' rule; tabs in groups (Lists' Your lists, Shared) have the group's
// name, muted, before its first.
// Use it for every page but a record (a record's head is RecordPage's, JobHeader today) and the board
// (BoardBand). It replaces Nav.tsx's app-local Header in Blueprint, and each place's own override of
// .screenhead (schedule.css, setup.css, bp-customers.css). Reads only --pagehead-*; Button, Figures and
// Skeleton read their own.
import { Fragment, type ReactNode } from 'react';
import { Skeleton, SkeletonGroup } from '../../components/Skeleton/Skeleton.tsx';
import './PageHead.css';

/** One step of the crumb: the place (a link back to its first page) or the page itself (no link). */
export interface Crumb {
  label: string;
  href?: string;
  onPress?: () => void;
}

/** One of the page's parts, as a tab under the title (Company: Getting started, Subscription, Your data). */
export interface PageTab {
  label: string;
  href?: string;
  onPress?: () => void;
  isCurrent?: boolean;
  /** What waits on it, muted. */
  count?: number;
  /**
   * What the count is, for screen readers ("1 at risk", starting with the count): the tab is read as
   * "Renewals 1 at risk", not "Renewals 1", its visible words still first in its name.
   */
  countLabel?: string;
  /** The group it starts or belongs to ("Your lists", "Shared"): a muted head before the group's first tab. */
  group?: string;
}

export interface PageHeadProps {
  /** Place › Page. The last step is the page and is never a link. */
  crumb?: readonly Crumb[];
  /** The page's one question ("Equipment we look after"). While loading, a Skeleton bar. */
  title: ReactNode;
  /** The h1's id, for the page's aria-labelledby. */
  titleId?: string;
  /** One muted line under the title. */
  lede?: ReactNode;
  /** At most two Buttons, the primary first. */
  actions?: ReactNode;
  /** The facts strip: ui-web's Figures (at most five). */
  figures?: ReactNode;
  /** The page's parts, as tabs; `label` names them for screen readers. */
  tabs?: { label: string; items: readonly PageTab[] };
  /** Draws the title and line as skeleton bars (the page hasn't its words yet). */
  isLoading?: boolean;
  /**
   * wide: the title 8 px under the crumb (or the top, with none) and the line 6 px under it, as Hours and
   * a Timesheet had them (not ruled; kept until Amir rules them one way).
   */
  gaps?: 'normal' | 'wide';
}

function go(c: { onPress?: () => void }) {
  return c.onPress
    ? (e: { preventDefault(): void }) => {
        e.preventDefault();
        c.onPress!();
      }
    : undefined;
}

export function PageHead({
  crumb,
  title,
  titleId,
  lede,
  actions,
  figures,
  tabs,
  isLoading = false,
  gaps = 'normal',
}: PageHeadProps) {
  return (
    <div className="fl-pagehead" data-gaps={gaps === 'wide' ? 'wide' : undefined}>
      <div className="fl-pagehead__row">
        <div className="fl-pagehead__words">
          {crumb?.length ? (
            <p className="fl-pagehead__crumb">
              {crumb.map((c, i) => {
                const last = i === crumb.length - 1;
                return (
                  <span key={`${c.label}-${i}`}>
                    {i > 0 ? <span aria-hidden="true"> › </span> : null}
                    {!last && (c.href || c.onPress) ? (
                      <a className="fl-pagehead__back" href={c.href ?? '#'} onClick={go(c)}>
                        {c.label}
                      </a>
                    ) : (
                      c.label
                    )}
                  </span>
                );
              })}
            </p>
          ) : null}
          <h1 className="fl-pagehead__title" id={titleId}>
            {isLoading ? (
              <SkeletonGroup label="Loading the page">
                <Skeleton shape="title" width="mid" />
              </SkeletonGroup>
            ) : (
              title
            )}
          </h1>
          {isLoading ? (
            <p className="fl-pagehead__lede">
              <Skeleton shape="line" width="long" />
            </p>
          ) : lede ? (
            <p className="fl-pagehead__lede">{lede}</p>
          ) : null}
        </div>
        {actions ? <div className="fl-pagehead__acts">{actions}</div> : null}
      </div>
      {figures ? <div className="fl-pagehead__figs">{figures}</div> : null}
      {tabs?.items.length ? (
        <nav className="fl-pagehead__tabs" aria-label={tabs.label}>
          {tabs.items.map((t, i) => (
            <Fragment key={t.label}>
              {t.group && t.group !== tabs.items[i - 1]?.group ? (
                <span className="fl-pagehead__group">{t.group}</span>
              ) : null}
              <a
                className="fl-pagehead__tab"
                href={t.href ?? '#'}
                aria-current={t.isCurrent ? 'page' : undefined}
                aria-label={t.count && t.countLabel ? `${t.label} ${t.countLabel}` : undefined}
                onClick={go(t)}
              >
                {t.label}
                {t.count ? <span className="fl-pagehead__cnt"> {t.count}</span> : null}
              </a>
            </Fragment>
          ))}
        </nav>
      ) : null}
    </div>
  );
}
